import React, { useState, useEffect } from 'react';
import { Wand2, Eye, Code } from 'lucide-react';
import FormModal from './FormModal';

interface ProcessMap {
  id: number;
  title: string;
  description: string;
  diagram_data: string;
  category: string;
  framework_id?: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface ProcessMapModalProps {
  onClose: () => void;
  onSubmit: (processMap: Partial<ProcessMap>) => Promise<void> | void;
  selectedProcessMap: ProcessMap | null;
}

export const ProcessMapModal: React.FC<ProcessMapModalProps> = ({
  onClose,
  onSubmit,
  selectedProcessMap,
}) => {
  const [processMap, setProcessMap] = useState<Partial<ProcessMap>>(
    selectedProcessMap || {
      title: '',
      description: '',
      diagram_data: '',
      category: '',
      is_active: true,
    }
  );

  const [loading, _setLoading] = useState(false);
  const [inputMode, setInputMode] = useState<'text' | 'json'>('text');
  const [processDescription, setProcessDescription] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);

  // Update state when selectedProcessMap changes
  useEffect(() => {
    if (selectedProcessMap) {
      setProcessMap(selectedProcessMap);
      // If editing existing process map, try to extract description from diagram_data
      if (selectedProcessMap.diagram_data) {
        try {
          const parsed = JSON.parse(selectedProcessMap.diagram_data);
          if (parsed.description) {
            setProcessDescription(parsed.description);
          }
        } catch (e) {
          // If not JSON, treat as raw description
          setProcessDescription(selectedProcessMap.description || '');
        }
      }
    }
  }, [selectedProcessMap]);

  // Generate diagram data from text description
  const generateDiagramData = async () => {
    if (!processDescription.trim()) {
      alert('Please enter a process description first');
      return;
    }

    setIsGenerating(true);
    try {
      // Legacy assistant endpoint removed.
      // Generate a deterministic local diagram payload from plain-text flow.
      const steps = processDescription
        .split(/\.|\n|->|then|, and then/gi)
        .map(step => step.trim())
        .filter(Boolean)
        .slice(0, 8);

      const nodes = [
        { id: 'start', label: 'Start', type: 'start', position: { x: 100, y: 100 } },
        ...steps.map((step, index) => ({
          id: `step_${index + 1}`,
          label: step.length > 64 ? `${step.slice(0, 61)}...` : step,
          type: 'process',
          position: { x: 280 + index * 180, y: 100 }
        })),
        { id: 'end', label: 'End', type: 'end', position: { x: 280 + steps.length * 180, y: 100 } }
      ];

      const edges = nodes.slice(0, -1).map((node, index) => {
        const nextNode = nodes[index + 1];
        return {
          id: `e_${index + 1}`,
          source: node.id,
          target: nextNode ? nextNode.id : node.id
        };
      });

      const generatedDiagram = { nodes, edges };
      const diagramData = JSON.stringify(generatedDiagram, null, 2);

      setProcessMap({ ...processMap, diagram_data: diagramData });
      setPreviewData(generatedDiagram);
      setInputMode('json'); // Switch to JSON view to show result
    } catch (error) {
      console.error('Error generating diagram:', error);
      // Fallback: create basic diagram structure
      const basicDiagram = {
        nodes: [
          { id: 'start', label: 'Start', type: 'start', position: { x: 100, y: 100 } },
          { id: 'process', label: processDescription.substring(0, 50) + '...', type: 'process', position: { x: 300, y: 100 } },
          { id: 'end', label: 'End', type: 'end', position: { x: 500, y: 100 } }
        ],
        edges: [
          { id: 'e1', source: 'start', target: 'process' },
          { id: 'e2', source: 'process', target: 'end' }
        ]
      };
      const diagramData = JSON.stringify(basicDiagram, null, 2);
      setProcessMap({ ...processMap, diagram_data: diagramData });
      setPreviewData(basicDiagram);
      setInputMode('json');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSubmit = async () => {
    try {
      await onSubmit(processMap);
      onClose();
    } catch {
      // error already handled by parent; keep modal open
    }
  };

  const isCreate = !selectedProcessMap?.id;

  return (
    <FormModal
      title={isCreate ? 'Create Process Map with AI' : 'Edit Process Map'}
      subtitle={isCreate ? 'Describe your process in plain English - AI will create the diagram' : 'Modify existing process map'}
      icon={<Wand2 className="text-white" size={24} />}
      onSubmit={handleSubmit}
      onCancel={onClose}
      submitText={isCreate ? 'Create Process Map' : 'Update Process Map'}
      cancelText="Cancel"
      loading={loading}
      size="large"
    >
      <div className="space-y-6">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">Title *</label>
          <input
            type="text"
            value={processMap.title || ''}
            onChange={(e) => setProcessMap({ ...processMap, title: e.target.value })}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
            placeholder="Enter process map title"
            disabled={loading}
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">Description</label>
          <textarea
            value={processMap.description || ''}
            onChange={(e) => setProcessMap({ ...processMap, description: e.target.value })}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
            rows={3}
            placeholder="Brief description of the process map"
            disabled={loading}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Category</label>
            <select
              value={processMap.category || ''}
              onChange={(e) => setProcessMap({ ...processMap, category: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
              disabled={loading}
            >
              <option value="">Select category</option>
              <option value="process">Process</option>
              <option value="documentation">Documentation</option>
              <option value="training">Training</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Active</label>
            <div className="flex items-center mt-3">
              <input
                type="checkbox"
                checked={processMap.is_active || false}
                onChange={(e) => setProcessMap({ ...processMap, is_active: e.target.checked })}
                className="h-5 w-5 text-slate-600 rounded focus:ring-2 focus:ring-slate-600 focus:ring-offset-0"
                disabled={loading}
              />
              <span className="ml-3 text-sm text-slate-700">Make this process map active</span>
            </div>
          </div>
        </div>

        {/* Input Mode Toggle */}
        <div className="space-y-4">
          <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
            <div className="flex items-center gap-2">
              <input
                type="radio"
                id="text-mode"
                name="input-mode"
                checked={inputMode === 'text'}
                onChange={() => setInputMode('text')}
                className="text-slate-600 focus:ring-slate-600"
              />
              <label htmlFor="text-mode" className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer">
                <Wand2 size={16} />
                AI Generation (Recommended)
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="radio"
                id="json-mode"
                name="input-mode"
                checked={inputMode === 'json'}
                onChange={() => setInputMode('json')}
                className="text-slate-600 focus:ring-slate-600"
              />
              <label htmlFor="json-mode" className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer">
                <Code size={16} />
                Manual JSON Editor
              </label>
            </div>
          </div>

          {inputMode === 'text' ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Process Description *</label>
                <textarea
                  value={processDescription}
                  onChange={(e) => setProcessDescription(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  rows={8}
                  placeholder="Describe your process in natural language. For example: 'The compliance process starts with document submission, then goes through initial review, detailed assessment, and final approval. If rejected, it goes back for revision.'"
                  disabled={loading || isGenerating}
                />
                <div className="flex items-center gap-2 text-xs text-blue-600">
                  <Wand2 size={14} />
                  <span>AI will analyze your description and create a professional process diagram</span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={generateDiagramData}
                  disabled={isGenerating || !processDescription.trim()}
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-all shadow-lg"
                >
                  {isGenerating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      AI Generating...
                    </>
                  ) : (
                    <>
                      <Wand2 size={18} />
                      Generate with AI
                    </>
                  )}
                </button>

                {previewData && (
                  <button
                    type="button"
                    onClick={() => setInputMode('json')}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
                  >
                    <Eye size={16} />
                    View JSON
                  </button>
                )}
              </div>

              {/* AI Branding */}
              <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                    <Wand2 className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">AI-Powered Generation</div>
                    <div className="text-xs text-slate-600">Our AI analyzes your description and creates a professional process diagram</div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Diagram Data *</label>
              <textarea
                value={processMap.diagram_data || ''}
                onChange={(e) => setProcessMap({ ...processMap, diagram_data: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all font-mono text-sm"
                rows={12}
                placeholder='{"nodes": [...], "edges": [...]}'
                disabled={loading}
              />
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-500">JSON format for process visualization</p>
                <button
                  type="button"
                  onClick={() => setInputMode('text')}
                  className="text-xs text-blue-600 hover:text-blue-700 underline"
                >
                  Switch to Text Mode
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </FormModal>
  );
};
